import { useIsFirstRender } from '@uidotdev/usehooks';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { styleVars } from '#/ui/utils/general';

export interface TransitionTimeouts {
  enter?: number;
  exit?: number;
  appear?: number;
}

export interface TransitionChildrenProps {
  className: string;
  style: React.CSSProperties;
}

export interface TransitionProps {
  /**
   * Controls whether the component will transition in.
   * @default false
   */
  in?: boolean;
  /**
   * Function that returns the children elements to render.
   * Receives TransitionChildrenProps as an argument.
   */
  children: (props: TransitionChildrenProps) => React.ReactNode;
  /**
   * Duration of the transition in milliseconds.
   * Can be a single number or an object specifying different durations.
   * @default 300
   */
  timeout?: number | TransitionTimeouts;
  /**
   * If true, component is mounted when entering.
   * @default false
   */
  mountOnEnter?: boolean;
  /**
   * If true, component is removed from the DOM after exiting.
   * @default false
   */
  unmountOnExit?: boolean;
  /**
   * Called immediately before the entering transition starts
   */
  onEnter?: () => void;
  /**
   * Called immediately before the exiting transition starts
   */
  onExit?: () => void;
  /**
   * Called after the entering transition completes
   */
  onEntered?: () => void;
  /**
   * Called after the exiting transition completes
   */
  onExited?: () => void;
  /**
   * Classes applied during different transition states
   */
  transitionClasses: {
    /**
     * Applied while the component is entering
     */
    enter?: string;
    /**
     * Applied while the component is exiting
     */
    exit?: string;
    /**
     * Applied throughout the entire transition
     */
    base?: string;
  };
}

export function Transition({
  in: inProp = false,
  children,
  timeout = 300,
  mountOnEnter = false,
  unmountOnExit = false,
  onEnter,
  onExit,
  onEntered,
  onExited,
  transitionClasses,
}: TransitionProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isRendered, setIsRendered] = React.useState(mountOnEnter && inProp);
  const isFirstRender = useIsFirstRender();

  const enterDuration = typeof timeout === 'number' ? timeout : (timeout.enter ?? 300);
  const exitDuration = typeof timeout === 'number' ? timeout : (timeout.exit ?? 300);
  const delay = typeof timeout === 'number' ? 100 : (timeout.appear ?? 100);

  React.useEffect(() => {
    let timer: NodeJS.Timeout;

    if (inProp) {
      // First, ensure the component is mounted
      setIsRendered(true);
      onEnter?.();

      // Then, in the next tick, start the transition
      timer = setTimeout(
        () => {
          requestAnimationFrame(() => {
            setIsVisible(true);
            onEntered?.();
          });
        },
        isFirstRender ? 100 : delay
      );
    } else {
      setIsVisible(false);
      onExit?.();

      timer = setTimeout(() => {
        if (unmountOnExit) {
          setIsRendered(false);
        }
        onExited?.();
      }, exitDuration);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [
    inProp,
    delay,
    exitDuration,
    unmountOnExit,
    onEnter,
    onExit,
    onEntered,
    onExited,
    isFirstRender,
  ]);

  const childrenProps = React.useMemo(
    () => ({
      className: twMerge(
        transitionClasses.base,
        isVisible && [transitionClasses.enter, 'duration-(--gooey-transition-duration-enter)'],
        !isVisible && [transitionClasses.exit, 'duration-(--gooey-transition-duration-exit)'],
        !isRendered && 'invisible'
      ),
      style: styleVars({
        '--gooey-transition-duration-enter': `${enterDuration}ms`,
        '--gooey-transition-duration-exit': `${exitDuration}ms`,
      }),
    }),
    [isVisible, enterDuration, exitDuration, transitionClasses, isRendered]
  );

  // not working... look at MUI transitions

  if (!isRendered && mountOnEnter) {
    return null;
  }

  return children(childrenProps);
}

Transition.displayName = 'GooeyTransition';

export default Transition;
